// client/src/pages/pmPortal/OwnerDistributionsPage.js
// What was collected, what came out of it, and what was sent on. Showing the workings
// rather than just the cheque is the point: an owner who can see the fee and the
// reserve does not have to ring the office to find out why the figure moved.
import React, { useEffect, useState } from 'react';
import { usePmPortalApiClient } from '../../hooks/usePmPortalApiClient';
import { PageHeader, Panel, Stat, Table, Loading, Notice, Pill, money, fmtDate, titleCase, statusTone }
    from '../../components/secure/pm/PMKit';

const OwnerDistributionsPage = () => {
    const api = usePmPortalApiClient();
    const [rows, setRows] = useState(null);
    const [error, setError] = useState('');

    useEffect(() => {
        api.get('/api/pm-portal/owner/draws')
            .then(setRows)
            .catch(e => setError(e.message));
    }, [api]);

    if (error) return <Notice tone="bad" title="Could not load your distributions" message={error} />;
    if (!rows) return <Loading />;

    const issued = rows.filter(r => r.status === 'issued' || r.status === 'cleared');
    const total = issued.reduce((s, r) => s + Number(r.draw_amount || 0), 0);
    const latest = rows[0];

    return (
        <>
            <PageHeader
                title="Distributions"
                subtitle="Funds released to you from your book, after operating costs, the management fee and any reserve."
            />

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
                <Stat label="Distributions issued" value={issued.length} />
                <Stat label="Total distributed" value={money(total)} />
                <Stat
                    label="Most recent"
                    value={latest ? money(latest.draw_amount) : '—'}
                    sub={latest ? `${titleCase(latest.status)} · period ending ${fmtDate(latest.period_end)}` : null}
                />
            </div>

            <Panel title="History">
                <Table
                    columns={[
                        { key: 'period_end', label: 'Period', nowrap: true,
                          render: r => `${fmtDate(r.period_start)} – ${fmtDate(r.period_end)}` },
                        { key: 'collected', label: 'Collected', align: 'right', render: r => money(r.collected) },
                        { key: 'operating_disbursements', label: 'Operating', align: 'right', render: r => money(r.operating_disbursements) },
                        { key: 'fees_charged', label: 'Management fee', align: 'right', render: r => money(r.fees_charged) },
                        { key: 'reserve_withheld', label: 'Reserve held', align: 'right', render: r => money(r.reserve_withheld) },
                        { key: 'draw_amount', label: 'Distributed', align: 'right',
                          render: r => <span className="font-semibold">{money(r.draw_amount)}</span> },
                        { key: 'status', label: 'Status',
                          render: r => <Pill tone={statusTone(r.status)}>{titleCase(r.status)}</Pill> },
                        { key: 'method', label: 'Method', render: r => titleCase(r.method) },
                        { key: 'issued_on', label: 'Issued', render: r => fmtDate(r.issued_on) },
                    ]}
                    rows={rows}
                    empty="No distribution has been computed yet."
                />
            </Panel>

            <p className="text-xs text-slate-500 mt-2">
                A distribution for a period is released only after that period&rsquo;s management fee has
                been authorised and swept. Paying an owner first would mean the fee came out of
                whatever was left, which in a pooled account belongs to other owners.
            </p>
        </>
    );
};

export default OwnerDistributionsPage;
